import React from 'react';
import { Label } from './label';
import { Input } from './input';
import { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import clsx from 'clsx';

interface ControllerInputProps<FieldsType extends FieldValues> {
  title?: string;
  titleIcon?: React.ReactNode;
  titleClassName?: string;
  name?: string;
  field: ControllerRenderProps<FieldsType, Path<FieldsType>>;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  errorMessage?: string;
  disabled: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  min?: number;
  max?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  hideNumberFieldArrows?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  compareValue?: {
    value?: number | string;
    color: 'success' | 'warning' | 'purple';
  };
  /** Filled by a paste from Excel and not saved yet. */
  isPasted?: boolean;
}

const ControllerInput = <FieldsType extends FieldValues>({
  title,
  field: { value, ...otherFieldValues },
  titleClassName,
  titleIcon,
  name,
  type,
  placeholder,
  errorMessage,
  disabled,
  icon,
  iconPosition = 'left',
  min,
  max,
  inputMode,
  hideNumberFieldArrows = false,
  onChange,
  compareValue,
  isPasted = false,
}: ControllerInputProps<FieldsType>) => (
  <>
    {title && (
      <div className="flex justify-between">
        <Label
          className={clsx(
            'text-gray-700 transition-all duration-200 ease-in-out',
            titleClassName,
          )}
          htmlFor={`input-${name}`}
        >
          {title}
        </Label>
        {titleIcon}
      </div>
    )}
    <div className="relative flex items-center">
      {icon && iconPosition === 'left' && (
        <div className="absolute left-3 pointer-events-none">{icon}</div>
      )}
      {icon && iconPosition === 'right' && (
        <div className="absolute right-3 pointer-events-none">{icon}</div>
      )}
      <Input
        id={`input-${name}`}
        value={value ?? ''}
        {...otherFieldValues}
        type={type}
        inputMode={inputMode}
        min={type === 'number' ? min : undefined}
        max={type === 'number' ? max : undefined}
        disabled={disabled}
        placeholder={placeholder}
        className={clsx(
          {
            'text-success-700 bg-success-50 border-success-200':
              compareValue?.color === 'success' &&
              compareValue?.value === value?.toString(),
            'text-warning-700 bg-warning-50 border-warning-200':
              compareValue?.color === 'warning' &&
              compareValue?.value === value?.toString(),
            'text-purple-700 bg-purple-50 border-purple-200':
              compareValue?.color === 'purple' &&
              compareValue?.value === value?.toString(),
          },
          'rounded-lg border-gray-300 gap-2 text-gray-900 placeholder-gray-500 disabled:placeholder-gray-100 focus-visible:outline-hidden focus-visible:shadow-active focus-visible:ring-offset-2 focus-visible:border-brand-300 disabled:bg-gray-100 md:text-base',
          {
            'bg-warning-50 border-warning-300 ring-2 ring-warning-200':
              isPasted,
            'border-error-300': errorMessage,
            'pl-10': icon && iconPosition === 'left',
            'pr-10': icon && iconPosition === 'right',
          },
          {
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none':
              hideNumberFieldArrows,
          },
        )}
        onChange={onChange}
      />
    </div>
  </>
);

export default ControllerInput;
